<template>
  <div>
    <b-card-title>{{ cardText }}</b-card-title>

    <b-row v-if="!shouldCrop">
      <b-col v-if="productThumbnail" cols="auto">
        <b-row>
          <b-col cols="auto">
            <b-img :src="productThumbnail" width="268" />
          </b-col>
        </b-row>
      </b-col>

      <b-col>
        <b-button @click="$refs.file.click()" :disabled="$cannot('product.create')" variant="primary">
          <input
            style="display:none;"
            type="file"
            ref="file"
            @change="pick($event)"
            accept="image/*"
            :disabled="$cannot('product.create')"
          />
          Pick Image
        </b-button>
      </b-col>
    </b-row>

    <template v-if="shouldCrop">
      <div class="nav-wizards-container">
        <b-nav class="nav-wizards-2 mb-2">
          <b-nav-item v-for="(item, key, index) in sizes" class="col" :key="key" :link-classes="stepClass(index + 1)">
            {{ item.name }}
          </b-nav-item>
        </b-nav>
      </div>

      <div v-for="({ width, height }, key, index) in sizes" :key="key">
        <b-card v-if="currentStep === index + 1">
          <cropper
            :ref="`cropper-${index}`"
            :src="thumbnail"
            :stencil-props="{
              handlers: {},
              movable: false,
              scalable: false,
            }"
            :stencil-size="{
              width: width,
              height: height,
            }"
            @change="args => cropperChange(key, args)"
          />
        </b-card>
      </div>

      <hr />

      <b-row align-h="end">
        <b-col cols="auto">
          <b-button v-if="firstStep" @click="cancelStep" class="mr-2">
            Cancel
          </b-button>

          <b-button v-if="!firstStep" @click="previousStep" class="mr-2">
            Back
          </b-button>

          <b-button v-if="!lastStep" variant="primary" @click="nextStep" class="mr-2">
            Next
          </b-button>

          <b-button v-if="lastStep" @click="upload" :disabled="$cannot('product.create')" variant="primary">
            Crop and Upload
          </b-button>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import { Cropper } from 'vue-advanced-cropper'

export default {
  name: 'ProductImagesThumbnail',
  components: { Cropper },
  props: { product: Object },
  inject: ['$cannot'],
  watch: {
    $route(value) {
      if (value.name !== 'products.edit.images') {
        this.thumbnail = ''
      }
    },
  },
  data() {
    return { thumbnail: '', blob: null, cropSizes: {}, startCropping: false }
  },
  computed: {
    cardText() {
      return 'Upload a product thumbnail'
    },

    productThumbnail() {
      return this.product?.thumbnail?.url
    },

    shouldCrop() {
      return this.startCropping
    },

    sizes() {
      return {
        thumb: { name: 'Thumbnail', width: 268, height: 149 },
        cart_thumb: { name: 'Cart', width: 124, height: 50 },
        cart_mini: { name: 'Mini Cart', width: 43, height: 43 },
        facebook_thumb: { name: 'Facebook', width: 600, height: 600 },
      }
    },

    totalSteps() {
      return Object.keys(this.sizes).length
    },

    currentStep() {
      return Number(this.$route.query.step ?? 0)
    },

    firstStep() {
      return this.currentStep <= 1
    },

    lastStep() {
      return this.currentStep === this.totalSteps
    },
  },
  methods: {
    pick(event) {
      // Reference to the DOM input element
      const input = event.target
      // Ensure that you have a file before attempting to read it
      if (input.files && input.files[0]) {
        // create a new FileReader to read this image and convert to base64 format
        const reader = new FileReader()
        reader.addEventListener('load', event => {
          this.nextStep()
          this.thumbnail = event.target.result
          this.startCropping = true
          this.blob = input.files[0]
        })

        // Start the reader job - read file as a data url (base64 format)
        reader.readAsDataURL(input.files[0])
      }
    },

    stepClass(step) {
      if (this.currentStep > step) {
        return 'completed'
      }

      return step === this.currentStep ? 'active' : 'disabled'
    },

    cancelStep() {
      this.startCropping = false
      return this.$router.push({ name: 'products.edit.images' })
    },

    previousStep() {
      if (this.currentStep <= 1) {
        return
      }

      return this.$router.push({
        name: 'products.edit.images',
        query: { step: this.currentStep - 1 },
      })
    },

    nextStep() {
      if (this.currentStep === this.totalSteps) {
        return
      }

      return this.$router.push({
        name: 'products.edit.images',
        query: { step: this.currentStep + 1 },
      })
    },

    cropperChange(key, { coordinates }) {
      this.cropSizes[key] = coordinates
    },

    async upload() {
      await this.$store.dispatch('products/updateThumb', {
        thumbnail: this.blob,
        sizes: this.cropSizes,
        id: this.$route.params.id,
      })
      this.startCropping = false

      this.$bvToast.toast(`A thumbnail was added to the product`, {
        title: 'Thumbnail updated',
        variant: 'primary',
      })
      this.$router.push({ name: 'products.edit.images', params: { id: this.$route.params.id } })
    },
  },
}
</script>
